import { useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuthStore, useCan } from "./stores/authStore";
import MetaTagsManager from "./components/MetaTagsManager";
import PublicStructuredData from "./components/seo/PublicStructuredData";
import useHashScroll from "./hooks/useHashScroll";
import useSessionExpiry from "./hooks/useSessionExpiry";
import { isAuthRoute } from "./lib/session";
import MaintenanceMode from "./components/MaintenanceMode";
import { Spinner } from "./components/ui/Spinner";
import { lazyWithRetry, clearChunkReloadFlag } from "./utils/lazyWithRetry";
import { isPmsGuidePath } from "./content/support/pmsSupportManifest";
import { signupPath } from "./lib/signupLead";

import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";

// --- Marketing routes (rendered in the unauthenticated branch) ---
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const ContactUs = lazyWithRetry(() => import("./pages/ContactUs"));
const ShopifySupportLandingPage = lazyWithRetry(() => import("./pages/ShopifySupportLandingPage"));
const ShopifyDocsPage = lazyWithRetry(() => import("./pages/ShopifyDocsPage"));
const DocsHubPage = lazyWithRetry(() => import("./pages/docs/DocsHubPage"));
const DocsArticlePage = lazyWithRetry(() => import("./pages/docs/DocsArticlePage"));
const DocsPrintPage = lazyWithRetry(() => import("./pages/docs/DocsPrintPage"));
const PmsIntegrationPage = lazyWithRetry(() => import("./pages/support/PmsIntegrationPage"));
const CallWidgetLandingPage = lazyWithRetry(() => import("./pages/CallWidgetLandingPage"));
const UseCasesIndex = lazyWithRetry(() => import("./pages/UseCasesIndex"));
const IndustryUseCasePage = lazyWithRetry(() => import("./pages/IndustryUseCasePage"));
const CaseStudiesIndex = lazyWithRetry(() => import("./pages/CaseStudiesIndex"));
const CaseStudyPage = lazyWithRetry(() => import("./pages/CaseStudyPage"));
const ComparisonsIndex = lazyWithRetry(() => import("./pages/ComparisonsIndex"));
const ComparisonPage = lazyWithRetry(() => import("./pages/ComparisonPage"));
const AlternativesHub = lazyWithRetry(() => import("./pages/AlternativesHub"));

const ChatWidgetMarketingPage = lazyWithRetry(() => import("./pages/ChatWidgetMarketingPage"));
const KnowledgeBaseMarketingPage = lazyWithRetry(() => import("./pages/KnowledgeBaseMarketingPage"));
const WorkflowBuilderMarketingPage = lazyWithRetry(() => import("./pages/WorkflowBuilderMarketingPage"));
const CallPipelineMarketingPage = lazyWithRetry(() => import("./pages/CallPipelineMarketingPage"));
const OutboundTriggersMarketingPage = lazyWithRetry(() => import("./pages/OutboundTriggersMarketingPage"));
const CRMMarketingPage = lazyWithRetry(() => import("./pages/CRMMarketingPage"));
const SalesCallPipelineLandingPage = lazyWithRetry(() => import("./pages/SalesCallPipelineLandingPage"));
const IndustrySignupPage = lazyWithRetry(() => import("./pages/IndustrySignupPage"));
const ManagedSupportMarketingPage = lazyWithRetry(() => import("./pages/ManagedSupportMarketingPage"));

// --- Legal pages ---
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));

// --- Auth/Onboarding ---
const Login = lazyWithRetry(() => import("./pages/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const AcceptInvitationPage = lazyWithRetry(() => import("./pages/AcceptInvitationPage"));
const OnboardingLoading = lazyWithRetry(() => import("./pages/OnboardingLoading"));
const SalesPipelineLaunch = lazyWithRetry(() => import("./pages/SalesPipelineLaunch"));
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));

const ShopifySSOCallback = lazyWithRetry(() => import("./pages/ShopifySSOCallback"));
const SsoCallback = lazyWithRetry(() => import("./pages/SsoCallback"));
const PublicTicketUpload = lazyWithRetry(() => import("./pages/PublicTicketUpload"));
const PublicOnboardingSetup = lazyWithRetry(() => import("./pages/PublicOnboardingSetup"));

// --- Blog (public) ---
const AllBlogsPage = lazyWithRetry(() => import("./pages/AllBlogsPage"));
const BlogDetailPage = lazyWithRetry(() => import("./pages/BlogDetailPage"));
const InboundVoiceGuidePage = lazyWithRetry(() => import("./pages/InboundVoiceGuidePage"));
const MissedCallCalculatorPage = lazyWithRetry(() => import("./pages/MissedCallCalculatorPage"));
const BlogPreviewPage = lazyWithRetry(() => import("./pages/BlogPreviewPage"));

// --- Dashboard chrome + pages (authenticated only) ---
const Layout = lazyWithRetry(() => import("./components/Layout"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const WorkflowPage = lazyWithRetry(() => import("./pages/WorkflowPage"));
const KnowledgeBase = lazyWithRetry(() => import("./pages/KnowledgeBase"));
const ChatWidgets = lazyWithRetry(() => import("./pages/ChatWidgets"));
const DirectMessagesPage = lazyWithRetry(() => import("./pages/DirectMessages"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const AIAgents = lazyWithRetry(() => import("./pages/AIAgents"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const OutboundCallingAccess = lazyWithRetry(() => import("./pages/OutboundCallingAccess"));
const ApiDocumentation = lazyWithRetry(() => import("./pages/ApiDocumentation"));
const Diagnostics = lazyWithRetry(() => import("./pages/Diagnostics"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const ClassificationHistory = lazyWithRetry(() => import("./pages/ClassificationHistory"));
const ShopifyFlowTester = lazyWithRetry(() => import("./pages/ShopifyFlowTester"));
const Integration = lazyWithRetry(() => import("./pages/Integration"));
const Subscription = lazyWithRetry(() => import("./pages/Subscription"));
const PaymentMethods = lazyWithRetry(() => import("./pages/PaymentMethods"));
const DataCollections = lazyWithRetry(() => import("./pages/DataCollections"));
const DataCollectionDetail = lazyWithRetry(() => import("./pages/DataCollectionDetail"));
const CampaignDetail = lazyWithRetry(() => import("./pages/CampaignDetail"));
const WhatsappCampaigns = lazyWithRetry(() => import("./pages/WhatsappCampaigns"));
const WhatsappCampaignDetail = lazyWithRetry(() => import("./pages/WhatsappCampaignDetail"));
const DataCollectionForm = lazyWithRetry(() => import("./pages/DataCollectionForm"));
const Calendars = lazyWithRetry(() => import("./pages/Calendars"));
const CalendarDetail = lazyWithRetry(() => import("./pages/CalendarDetail"));
const AgentsPage = lazyWithRetry(() => import("./pages/AgentsPage"));
const SupervisorsPage = lazyWithRetry(() => import("./pages/SupervisorsPage"));
const AdminsPage = lazyWithRetry(() => import("./pages/AdminsPage"));
const PermissionsPage = lazyWithRetry(() => import("./pages/PermissionsPage"));
const AgentAnalyticsPage = lazyWithRetry(() => import("./pages/AgentAnalyticsPage"));
const TwilioVoiceTest = lazyWithRetry(() => import("./pages/TwilioVoiceTest"));
const CallWidgets = lazyWithRetry(() => import("./pages/CallWidgets"));
const CallCentreDialer = lazyWithRetry(() => import("./pages/callCentre/Dialer"));
const CallCentreQueue = lazyWithRetry(() => import("./pages/callCentre/Queue"));
const CallCentreLeads = lazyWithRetry(() => import("./pages/callCentre/Leads"));
const CallCentreIncoming = lazyWithRetry(() => import("./pages/callCentre/Incoming"));
const CallCentreMissed = lazyWithRetry(() => import("./pages/callCentre/Missed"));
const CallCentreCallbacks = lazyWithRetry(() => import("./pages/callCentre/Callbacks"));
const CallCentreHistory = lazyWithRetry(() => import("./pages/callCentre/History"));
const CallCentreScripts = lazyWithRetry(() => import("./pages/callCentre/Scripts"));
const CallCentreTemplates = lazyWithRetry(() => import("./pages/callCentre/Templates"));
const CallCentreFollowUps = lazyWithRetry(() => import("./pages/callCentre/FollowUps"));
const CallCentreUsage = lazyWithRetry(() => import("./pages/callCentre/Usage"));
const CallCentreFloor = lazyWithRetry(() => import("./pages/callCentre/Floor"));
const CallCentreRouting = lazyWithRetry(() => import("./pages/callCentre/Routing"));
const CallCentreNumbers = lazyWithRetry(() => import("./pages/callCentre/Numbers"));
const CallWidgetSetupWizard = lazyWithRetry(() => import("./pages/CallWidgetSetupWizard"));
const UnifiedInbox = lazyWithRetry(() => import("./pages/Inbox"));
const CRMTickets = lazyWithRetry(() => import("./pages/crm/Tickets"));
const CRMContacts = lazyWithRetry(() => import("./pages/crm/Contacts"));
const CRMSupervisorAnalytics = lazyWithRetry(() => import("./pages/crm/SupervisorAnalytics"));
const CRMAgentAnalytics = lazyWithRetry(() => import("./pages/crm/AgentAnalytics"));
const CRMSelfAnalytics = lazyWithRetry(() => import("./pages/crm/SelfAnalytics"));
const CRMSettings = lazyWithRetry(() => import("./pages/crm/Settings"));
const LiveCallsPage = lazyWithRetry(() => import("./pages/support/LiveCalls"));
const WidgetCustomizer = lazyWithRetry(() => import("./pages/WidgetCustomizer"));
const Referrals = lazyWithRetry(() => import("./pages/Referrals"));
const VisitorAnalyticsDashboard = lazyWithRetry(() => import("./pages/VisitorAnalyticsDashboard"));
const ManagedSupport = lazyWithRetry(() => import("./pages/ManagedSupport"));

const PublicCallSurvey = lazyWithRetry(() => import("./pages/PublicCallSurvey"));


const RouteFallback = () => <div className="min-h-screen bg-slate-900" />;

function App() {
    const { isAuthenticated, initializeAuth, orgRole, userRole, _hasHydrated, sessionExpired } = useAuthStore();
    const can = useCan();
    const location = useLocation();

    // Initialize auth state on app startup
    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    useSessionExpiry();
    // Note: fetchMe polling is handled in Layout.tsx (every 5 minutes) — no duplicate here

    useEffect(() => {
        clearChunkReloadFlag();
    }, []);

    useHashScroll();

    // Handle admin routes separately
    if (location.pathname.startsWith("/hq")) {
        return <AdminApp />;
    }

    // CRM ticket attachment upload — chromeless public page reachable by
    // customers via a signed link. Must short-circuit before the auth gate
    // so logged-out customers can complete the upload without redirecting
    // to the marketing site.
    if (location.pathname.startsWith("/upload-files/")) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/upload-files/:token" element={<PublicTicketUpload />} />
                </Routes>
            </Suspense>
        );
    }

    if (location.pathname.startsWith("/setup/")) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/setup/:token" element={<PublicOnboardingSetup />} />
                </Routes>
            </Suspense>
        );
    }

    // Public survey route — token-authenticated, intentionally rendered
    // before the auth-hydration gate so an SMS recipient never sees the
    // landing page on cold load.
    if (location.pathname.startsWith("/survey/call/")) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/survey/call/:token" element={<PublicCallSurvey />} />
                </Routes>
            </Suspense>
        );
    }

    // Docs are auth-agnostic, so they short-circuit ahead of the auth gate.
    // Registering them in the unauthenticated branch alone would strand
    // signed-in customers on that branch's `*` → /dashboard redirect, and
    // registering them in the authenticated branch would wrap a marketing-chrome
    // page in the dashboard Layout. One block serves logged-out readers,
    // signed-in customers, the prerender crawler and the PDF build alike.
    if (location.pathname === "/docs" || location.pathname.startsWith("/docs/")) {
        return (
            <>
                <MetaTagsManager />
                <PublicStructuredData />
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/docs" element={<DocsHubPage />} />
                        <Route path="/docs/shopify" element={<ShopifyDocsPage />} />
                        <Route path="/docs/get-started/:slug" element={<DocsArticlePage tier="quickstart" />} />
                        <Route path="/docs/manual/:slug" element={<DocsArticlePage tier="manual" />} />
                        <Route path="/docs/print/:tier" element={<DocsPrintPage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </>
        );
    }

    // The PMS integration guides, short-circuited for the same reason as
    // /docs. These are marketplace prerequisites — Cloudbeds will not schedule
    // a certification call without a public URL — so they must resolve for a
    // logged-out reviewer, a signed-in customer, and the prerender crawler
    // without any of them being bounced to /dashboard or /.
    //
    // Matched by slug, not by `/support/` prefix, so the dashboard keeps
    // /support/live-calls.
    if (isPmsGuidePath(location.pathname)) {
        return (
            <>
                <MetaTagsManager />
                <PublicStructuredData />
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/support/:slug" element={<PmsIntegrationPage />} />
                    </Routes>
                </Suspense>
            </>
        );
    }

    // flogpt.com only. The page renders not-found on the India build, with no
    // hreflang. Short-circuited like /docs so a signed-in visitor is not
    // bounced to the dashboard, and the page is not wrapped in Layout.
    if (location.pathname === "/tools/missed-call-calculator") {
        return (
            <>
                <MetaTagsManager />
                <PublicStructuredData />
                <Suspense fallback={<RouteFallback />}>
                    <MissedCallCalculatorPage />
                </Suspense>
            </>
        );
    }

    // Wait for zustand persist to rehydrate auth state before rendering routes
    // This prevents the race condition where isAuthenticated=false on first render
    // causes a redirect to "/" before the persisted auth state is restored
    if (!_hasHydrated) {
        return null;
    }

    // A session that ended on its own, rather than by the user signing out.
    // Without this the catch-all below would drop them on the marketing site
    // with no explanation of why their dashboard vanished. Placed after the
    // token-authenticated public routes, which belong to people who were never
    // signed in here.
    if (!isAuthenticated && sessionExpired && !isAuthRoute(location.pathname)) {
        return <Navigate to="/login" replace />;
    }

    // Show landing page for non-authenticated users
    if (!isAuthenticated) {
        return (
            <>
                <MetaTagsManager />
                <PublicStructuredData />
                <MaintenanceMode />

                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                        <Route path="/auth/shopify-sso" element={<ShopifySSOCallback />} />
                        <Route path="/auth/sso" element={<SsoCallback />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/contact" element={<ContactUs />} />
                        <Route path="/shopify-ai-customer-support" element={<ShopifySupportLandingPage />} />
                        <Route path="/ai-voice-agents" element={<CallWidgetLandingPage />} />
                        <Route path="/ai-chat-widget" element={<ChatWidgetMarketingPage />} />
                        <Route path="/ai-knowledge-base" element={<KnowledgeBaseMarketingPage />} />
                        <Route path="/ai-workflow-builder" element={<WorkflowBuilderMarketingPage />} />
                        <Route path="/ai-call-pipeline" element={<CallPipelineMarketingPage />} />
                        <Route path="/ai-outbound-triggers" element={<OutboundTriggersMarketingPage />} />
                        <Route path="/ai-crm" element={<CRMMarketingPage />} />
                        <Route path="/sales-call-pipeline" element={<SalesCallPipelineLandingPage />} />
                        <Route path="/get-started" element={<IndustrySignupPage />} />
                        <Route path="/get-started/:campaignSlug" element={<IndustrySignupPage />} />
                        <Route path="/home-services" element={<HomeServicesLegacyRedirect />} />
                        <Route path="/managed-support" element={<ManagedSupportMarketingPage />} />
                        <Route path="/use-cases" element={<UseCasesIndex />} />
                        <Route path="/use-cases/:industrySlug" element={<IndustryUseCasePage />} />
                        <Route path="/case-studies" element={<CaseStudiesIndex />} />
                        <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
                        <Route path="/compare" element={<ComparisonsIndex />} />
                        <Route path="/compare/:comparisonSlug" element={<ComparisonPage />} />
                        <Route path="/alternatives" element={<AlternativesHub />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/cookies" element={<Cookies />} />
                        <Route path="/blogs" element={<AllBlogsPage />} />
                        {/* Static how-to. More specific than /blog/:slug so it does not wait on the CMS. */}
                        <Route path="/blog/how-to-set-up-ai-voice-agent-inbound-calls" element={<InboundVoiceGuidePage />} />
                        <Route path="/blog/:slug" element={<BlogDetailPage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </>
        );
    }

    return (
        <>
            <MetaTagsManager />
            <MaintenanceMode />
            <Suspense
                fallback={
                    <div className="flex items-center justify-center h-screen bg-slate-50">
                        <Spinner />
                    </div>
                }
            >
                <Layout>
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center h-64">
                                <Spinner />
                            </div>
                        }
                    >
                        <Routes>
                            <Route path="/onboarding" element={<OnboardingLoading />} />
                            <Route path="/launch/sales-pipeline" element={<SalesPipelineLaunch />} />
                            {/* Also registered here: a logged-in visitor arriving from ad 54
                                would otherwise fall through `*` to /dashboard. */}
                            <Route path="/sales-call-pipeline" element={<SalesCallPipelineLandingPage />} />
                            <Route path="/auth/shopify-sso" element={<ShopifySSOCallback />} />
                            {/* Same reason as above: a signed-in user re-opening
                                the callback must not fall through to `*`. */}
                            <Route path="/auth/sso" element={<SsoCallback />} />
                            {/* `/get-started/*` and `/home-services` are deliberately
                                not here, for the same reason `/signup` is not: these
                                routes render inside Layout, and an existing customer
                                cannot finish a signup form anyway. `*` takes them to
                                the dashboard. */}
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/workflows" element={<WorkflowPage />} />
                            <Route path="/workflows/:id" element={<WorkflowPage />} />
                            <Route path="/workflows/templates" element={<WorkflowPage />} />
                            <Route path="/knowledge-base" element={<Navigate to="/knowledge-base/overview" replace />} />
                            <Route path="/knowledge-base/:tab" element={<KnowledgeBase />} />
                            <Route path="/chat-widgets/:id/customize" element={<WidgetCustomizer />} />
                            <Route path="/chat-widgets/whatsapp-campaigns" element={<WhatsappCampaigns />} />
                            <Route path="/chat-widgets/whatsapp-campaigns/:campaignId" element={<WhatsappCampaignDetail />} />
                            <Route path="/chat-widgets/whatsapp-campaigns/:campaignId/:tab" element={<WhatsappCampaignDetail />} />
                            <Route path="/chat-widgets" element={<Navigate to="/chat-widgets/widgets" replace />} />
                            <Route path="/chat-widgets/:tab" element={<ChatWidgets />} />
                            <Route path="/call" element={<Navigate to="/call/widget" replace />} />
                            <Route path="/call/setup" element={<CallWidgetSetupWizard />} />
                            <Route path="/call/campaigns/:campaignId" element={<CampaignDetail />} />
                            <Route path="/call/campaigns/:campaignId/:tab" element={<CampaignDetail />} />
                            <Route path="/call/:tab" element={<CallWidgets />} />

                            {/* The call centre is native now. `/call/call-center`
                                used to hand off to the standalone app. */}
                            <Route path="/call-centre" element={<Navigate to="/call-centre/dialer" replace />} />
                            <Route path="/call-centre/dialer" element={<CallCentreDialer />} />
                            <Route path="/call-centre/queue" element={<CallCentreQueue />} />
                            <Route path="/call-centre/leads" element={<CallCentreLeads />} />
                            <Route path="/call-centre/incoming" element={<CallCentreIncoming />} />
                            <Route path="/call-centre/missed" element={<CallCentreMissed />} />
                            <Route path="/call-centre/callbacks" element={<CallCentreCallbacks />} />
                            <Route path="/call-centre/history" element={<CallCentreHistory />} />
                            <Route path="/call-centre/scripts" element={<CallCentreScripts />} />
                            <Route path="/call-centre/templates" element={<CallCentreTemplates />} />
                            <Route path="/call-centre/follow-ups" element={<CallCentreFollowUps />} />
                    <Route path="/call-centre/usage" element={<CallCentreUsage />} />
                            <Route path="/call-centre/floor" element={<CallCentreFloor />} />
                            <Route path="/call-centre/routing" element={<CallCentreRouting />} />
                            <Route path="/call-centre/numbers" element={<CallCentreNumbers />} />
                            <Route
                                path="/call/call-center"
                                element={<Navigate to="/call-centre/dialer" replace />}
                            />
                            {/* Backward-compatibility: old /call-widgets URLs redirect to /call. */}
                            <Route path="/call-widgets" element={<Navigate to="/call/widget" replace />} />
                            <Route path="/call-widgets/:tab" element={<CallWidgetsLegacyRedirect />} />
                            <Route path="/support-chats" element={<DirectMessagesPage />} />
                            <Route path="/managed-support" element={<ManagedSupport />} />
                            <Route path="/support/live-calls" element={<LiveCallsPage />} />
                            <Route
                                path="/support-tickets"
                                element={<Navigate to={`/crm/tickets${location.search}`} replace />}
                            />
                            <Route path="/inbox" element={<UnifiedInbox />} />
                            <Route path="/crm/tickets" element={<CRMTickets />} />
                            <Route path="/crm/contacts" element={<CRMContacts />} />
                            <Route path="/crm/analytics/self" element={<CRMSelfAnalytics />} />
                            {(orgRole === "supervisor" || orgRole === "owner") && (
                                <Route path="/crm/analytics/agent" element={<CRMAgentAnalytics />} />
                            )}
                            {orgRole === "owner" && (
                                <Route path="/crm/analytics/supervisor" element={<CRMSupervisorAnalytics />} />
                            )}
                            <Route path="/crm/settings" element={<CRMSettings />} />
                            <Route path="/ai-agents" element={<AIAgents />} />
                            <Route path="/analytics" element={<Navigate to="/analytics/overview" replace />} />
                            <Route path="/analytics/:tab" element={<Analytics />} />
                            <Route path="/visitor-dashboard" element={<VisitorAnalyticsDashboard />} />
                            <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                            <Route path="/settings/outbound-calling" element={<OutboundCallingAccess />} />
                            <Route path="/settings/:tab" element={<Settings />} />
                            <Route path="/api-documentation" element={<ApiDocumentation />} />
                            {/* Not `/setup/diagnostics`: `/setup/*` is claimed
                                above by the public onboarding branch. The page
                                gates on role itself, like the other
                                direct-URL-reachable routes here. */}
                            <Route path="/diagnostics" element={<Diagnostics />} />
                            <Route path="/integration" element={<Navigate to="/integration/overview" replace />} />
                            <Route path="/integration/:tab" element={<Integration />} />
                            {/* Unlike the sidebar entries these were reachable by direct
                                URL at any role, so the guard is the permission alone —
                                pairing it with a role check here would newly lock out
                                editors and viewers who can open them today. */}
                            {can("viewBilling") && (
                                <>
                                    <Route path="/subscription" element={<Subscription />} />
                                    <Route path="/payment-methods" element={<PaymentMethods />} />
                                </>
                            )}
                            <Route path="/pricing" element={<Pricing />} />
                            {can("viewDataCollections") && (
                                <>
                                    <Route path="/data-collections" element={<DataCollections />} />
                                    <Route path="/data-collections/:id" element={<DataCollectionDetail />} />
                                </>
                            )}
                            {can("manageDataCollections") ? (
                                <>
                                    <Route path="/data-collections/new" element={<DataCollectionForm />} />
                                    <Route path="/data-collections/:id/edit" element={<DataCollectionForm />} />
                                </>
                            ) : (
                                // Dropping the route entirely would let `:id` swallow
                                // /data-collections/new and try to load a collection
                                // called "new", so send the editor paths somewhere real.
                                <>
                                    <Route
                                        path="/data-collections/new"
                                        element={<Navigate to="/data-collections" replace />}
                                    />
                                    <Route
                                        path="/data-collections/:id/edit"
                                        element={<Navigate to="/data-collections" replace />}
                                    />
                                </>
                            )}
                            <Route path="/calendars" element={<Calendars />} />
                            <Route path="/calendars/:id" element={<CalendarDetail />} />
                            <Route path="/twilio-voice-test" element={<TwilioVoiceTest />} />
                            <Route path="/referrals" element={<Navigate to="/referrals/links" replace />} />
                            <Route path="/referrals/:tab" element={<Referrals />} />
                            {orgRole === "owner" && can("viewTeam") && (
                                <>
                                    <Route path="/agents" element={<AgentsPage />} />
                                    <Route path="/supervisors" element={<SupervisorsPage />} />
                                </>
                            )}
                            {/* Admins, unlike agents and supervisors, are admin-manageable:
                                inviteAdmin, createAdminWithCredentials and
                                updateAdminActiveStatus all accept owner or admin. */}
                            {(orgRole === "owner" || orgRole === "admin") && can("viewTeam") && (
                                <>
                                    <Route path="/admins" element={<AdminsPage />} />
                                    {/* Owner-only to edit, but an admin who follows a
                                        shared link gets the panel's read-only note
                                        rather than a bounce to the dashboard. */}
                                    <Route path="/users/permissions" element={<PermissionsPage />} />
                                </>
                            )}
                            {(orgRole === "supervisor" || orgRole === "owner") && <Route path="/agent-analytics" element={<AgentAnalyticsPage />} />}
                            {userRole === "super_admin" && (
                                <>
                                    <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
                                    <Route path="/admin/classifications" element={<ClassificationHistory />} />
                                    <Route path="/admin/:tab" element={<AdminDashboard />} />
                                </>
                            )}
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Suspense>
                </Layout>
            </Suspense>
        </>
    );
}

/** The one alias that predates the `/get-started` prefix. Keeps the ad click params. */
function HomeServicesLegacyRedirect() {
    const { search, hash } = useLocation();
    return <Navigate to={signupPath("home-services", search, hash)} replace />;
}

function CallWidgetsLegacyRedirect() {
    const { tab } = useParams<{ tab: string }>();
    const newTab = tab === "widgets" ? "widget" : (tab ?? "widget");
    return <Navigate to={`/call/${newTab}`} replace />;
}

function AdminApp() {
    return (
        <Suspense
            fallback={
                <div className="flex items-center justify-center h-screen bg-slate-50">
                    <Spinner />
                </div>
            }
        >
            <Routes>
                <Route path="/hq/login" element={<AdminLogin />} />
                <Route path="/hq/dashboard" element={<Navigate to="/hq/dashboard/overview" replace />} />
                <Route path="/hq/dashboard/:tab" element={<AdminDashboard />} />
                <Route path="/hq/classifications" element={<ClassificationHistory />} />
                <Route path="/hq/shopify-tester" element={<ShopifyFlowTester />} />
                <Route path="/hq/blog/preview" element={<BlogPreviewPage />} />
                <Route path="/hq/*" element={<Navigate to="/hq/login" replace />} />
            </Routes>
        </Suspense>
    );
}

export default App;
